import React from "react";
import { Gavel } from "lucide-react";

/**
 * DecisionGate — the closing card on every worksheet. A radio group of the
 * gate's options, a required commitment textarea, and an optional review
 * date. The card is visually loud on purpose — the founder's decision is
 * the point of the worksheet, and the whole experience should feel like it.
 */
export default function DecisionGate({
  gate,
  value,
  onChange,
  onBlur,
  highlight = false,
}) {
  // Only auto-focus the Other input when the user picks Other in-session.
  // On initial render we leave focus alone so the viewport doesn't jump
  // down to a pre-filled Other on sheets like Founder Equity & Control.
  // Hook must be declared before any early return.
  const [otherFocus, setOtherFocus] = React.useState(false);
  if (!gate) return null;
  const options = gate.options || [];
  const current = value || {};
  const isPreset = options.includes(current.selected_option);
  const isOther = !isPreset && !!current.selected_option;

  const patch = (p) => onChange({ ...current, ...p });

  return (
    <section
      id="decision-gate"
      data-testid="decision-gate"
      className={`relative rounded-xl border p-6 sm:p-8 shadow-xl overflow-hidden scroll-mt-24 ${
        highlight
          ? "border-pink-400 ring-2 ring-pink-400/50"
          : "border-shell-accent/50"
      }`}
      style={{
        background:
          "linear-gradient(140deg, hsl(45 100% 96%), hsl(0 0% 100%) 65%)",
      }}
    >
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[color:var(--brand-accent,#E91E63)]/10 blur-2xl pointer-events-none" />
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-full bg-[color:var(--brand-accent,#E91E63)]/15 grid place-items-center">
          <Gavel className="w-4 h-4 text-[color:var(--brand-accent,#E91E63)]" />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--brand-accent,#E91E63)] font-semibold">
          Decision Gate
        </div>
      </div>
      <h3 className="text-xl sm:text-2xl font-display leading-tight max-w-2xl">
        {gate.question}
      </h3>

      <div className="mt-6 space-y-2.5">
        {options.map((opt) => {
          const id = `gate-opt--${opt}`;
          const active = current.selected_option === opt;
          return (
            <label
              key={opt}
              htmlFor={id}
              className={`flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition ${
                active
                  ? "border-pink-300/70 bg-muted"
                  : "border-border hover:border-foreground/30"
              }`}
            >
              <input
                id={id}
                type="radio"
                name="decision-gate-option"
                checked={active}
                onChange={() => patch({ selected_option: opt })}
                onBlur={onBlur}
                className="mt-1 accent-[color:var(--brand-accent,#E91E63)]"
                data-testid={`gate-option-${options.indexOf(opt)}`}
              />
              <span className="text-sm text-foreground">{opt}</span>
            </label>
          );
        })}
        {/* An "Other" free-form line so founders can capture nuance the fixed
            options miss — mirrors the pattern in single_select fields. */}
        <label
          className={`flex flex-col gap-2 rounded-md border px-3 py-2.5 ${
            isOther ? "border-pink-300/70 bg-muted" : "border-border hover:border-foreground/30"
          }`}
        >
          <div className="flex items-center gap-3">
            <input
              type="radio"
              name="decision-gate-option"
              checked={isOther}
              onChange={() => { setOtherFocus(true); patch({ selected_option: current.other_text ? `Other: ${current.other_text}` : "Other: " }); }}
              className="accent-[color:var(--brand-accent,#E91E63)]"
            />
            <span className="text-sm text-foreground">Other</span>
          </div>
          {isOther && (
            <input
              type="text"
              autoFocus={otherFocus}
              value={current.other_text || (current.selected_option?.startsWith("Other: ") ? current.selected_option.slice(7) : "")}
              onChange={(e) => patch({
                other_text: e.target.value,
                selected_option: `Other: ${e.target.value}`,
              })}
              onBlur={onBlur}
              placeholder="Describe your choice…"
              className="w-full rounded-md border border-border bg-white px-2 py-1.5 text-sm outline-none focus:border-pink-300"
            />
          )}
        </label>
      </div>

      <div className="mt-6">
        <label className="block mb-2">
          <span className="text-sm font-medium text-foreground">
            {gate.commitment_label || "Write your commitment"}
          </span>
        </label>
        <textarea
          rows={4}
          value={current.commitment ?? ""}
          onChange={(e) => patch({ commitment: e.target.value })}
          onBlur={onBlur}
          className="w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm outline-none focus:border-pink-300 focus:ring-1 focus:ring-pink-300/50 resize-y leading-relaxed"
          data-testid="gate-commitment"
        />
      </div>

      {gate.requires_review_date && (
        <div className="mt-5">
          <label className="block mb-2">
            <span className="text-sm font-medium text-foreground">Review date</span>
            <p className="mt-0.5 text-xs text-muted-foreground">
              When will you revisit this decision?
            </p>
          </label>
          <input
            type="date"
            value={current.review_date ?? ""}
            onChange={(e) => patch({ review_date: e.target.value })}
            onBlur={onBlur}
            className="rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-pink-300"
            data-testid="gate-review-date"
          />
        </div>
      )}
    </section>
  );
}
